import { useState, useMemo } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { KeyRound, Save, Eye, EyeOff, Lock, ShieldCheck, AlertCircle, CheckCircle2 } from "lucide-react";

const strengthOf = (pw) => {
  if (!pw) return { score: 0, label: "—", color: "rgba(255,255,255,0.15)" };
  let s = 0;
  if (pw.length >= 6) s++;
  if (pw.length >= 10) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const map = [
    { label: "Too short", color: "#ef4444" },
    { label: "Weak", color: "#f59e0b" },
    { label: "Fair", color: "#fbbf24" },
    { label: "Good", color: "#34d399" },
    { label: "Strong", color: "#10b981" },
    { label: "Excellent", color: "#059669" },
  ];
  return { score: s, ...map[s] };
};

export default function ChangePassword() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState({ c: false, n: false, x: false });
  const [busy, setBusy] = useState(false);

  const strength = useMemo(() => strengthOf(next), [next]);
  const matches = next && confirm && next === confirm;
  const mismatch = confirm && next !== confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/agent/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="agent-change-password-page" className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[2px] text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1">
          <ShieldCheck size={11} /> Account Security
        </span>
        <h1 className="text-[28px] font-black tracking-tight mt-3 inline-flex items-center gap-3 leading-none">
          <KeyRound size={26} className="text-emerald-300" /> Change Password
        </h1>
        <p className="text-sm text-white/55 mt-2">Update your Agent Panel login password. Use a strong, unique combination.</p>
      </div>

      <div className="relative overflow-hidden rounded-2xl border border-white/[0.08]"
        style={{ background: "linear-gradient(180deg, rgba(13,22,38,0.92) 0%, rgba(10,17,30,0.92) 100%)" }}
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent" />
        <div aria-hidden className="absolute -top-24 -right-24 h-60 w-60 bg-emerald-400/10 rounded-full blur-[80px]" />
        <form onSubmit={submit} className="relative p-6 space-y-5" data-testid="agent-change-password-form">
          <PasswordField
            label="Current password"
            value={current}
            setValue={setCurrent}
            show={show.c}
            toggle={() => setShow((s) => ({ ...s, c: !s.c }))}
            testid="agent-cp-current"
            placeholder="Enter your existing password"
          />

          <div>
            <PasswordField
              label="New password"
              value={next}
              setValue={setNext}
              show={show.n}
              toggle={() => setShow((s) => ({ ...s, n: !s.n }))}
              testid="agent-cp-new"
              placeholder="Minimum 6 characters"
            />

            {/* Strength meter */}
            {next && (
              <div className="mt-2.5">
                <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="flex-1 transition-all duration-300"
                      style={{ background: i < strength.score ? strength.color : "transparent" }}
                    />
                  ))}
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[10.5px]">
                  <span className="text-white/40">Password strength</span>
                  <span className="font-bold" style={{ color: strength.color }}>{strength.label}</span>
                </div>
              </div>
            )}
          </div>

          <div>
            <PasswordField
              label="Confirm new password"
              value={confirm}
              setValue={setConfirm}
              show={show.x}
              toggle={() => setShow((s) => ({ ...s, x: !s.x }))}
              testid="agent-cp-confirm"
              placeholder="Re-enter the new password"
              error={mismatch}
            />
            {mismatch && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-rose-300">
                <AlertCircle size={11} /> Passwords don't match
              </div>
            )}
            {matches && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-emerald-300">
                <CheckCircle2 size={11} /> Looks good
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={busy || !current || !next || next !== confirm || next.length < 6}
            className="group relative w-full h-12 rounded-xl text-[#06140b] font-extrabold inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all overflow-hidden"
            style={{ background: "linear-gradient(135deg,#34d399 0%,#10b981 50%,#059669 100%)", boxShadow: "0 14px 32px -12px rgba(16,185,129,0.55)" }}
            data-testid="agent-cp-submit"
          >
            <span aria-hidden className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent translate-x-[-150%] group-hover:translate-x-[150%] transition-transform duration-700" />
            <Save size={15} className="relative" strokeWidth={2.5} />
            <span className="relative text-[14px]">{busy ? "Saving…" : "Update password"}</span>
          </button>

          {/* Security tips */}
          <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3.5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck size={13} className="text-emerald-400" />
              <div className="text-[10.5px] uppercase tracking-[1.6px] text-white/65 font-bold">Security tips</div>
            </div>
            <ul className="space-y-1 text-[11.5px] text-white/55">
              <li className="flex items-start gap-2"><span className="h-1 w-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" /> Use a mix of uppercase, lowercase, numbers, and symbols.</li>
              <li className="flex items-start gap-2"><span className="h-1 w-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" /> Avoid reusing passwords from other websites.</li>
              <li className="flex items-start gap-2"><span className="h-1 w-1 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" /> Maxlive88 staff will never ask you for your password.</li>
            </ul>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordField({ label, value, setValue, show, toggle, testid, placeholder, error }) {
  return (
    <div>
      <label className="block text-[10.5px] uppercase tracking-[1.5px] text-white/55 mb-1.5 font-bold">{label}</label>
      <div className="relative group">
        <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-emerald-400 transition" />
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          className={`w-full h-12 pl-10 pr-11 bg-[#050a14] border rounded-xl text-sm focus:outline-none focus:ring-4 transition ${
            error ? "border-rose-500/50 focus:border-rose-500/70 focus:ring-rose-500/15" : "border-white/10 focus:border-emerald-500/60 focus:ring-emerald-500/10"
          }`}
          data-testid={testid}
        />
        <button type="button" onClick={toggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 grid place-items-center rounded-md text-white/45 hover:text-white hover:bg-white/5 transition"
          tabIndex={-1}
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}
